import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tag, Plus, Trash2, Loader2, Pencil } from 'lucide-react';

const PRODUCTS = { regen_plus: 'YourReGen+', protocol: 'YourReGen Protocol' };
const BEST_VALUES = [
  { v: '', label: 'None' },
  { v: 'monthly', label: 'Monthly' },
  { v: 'annual', label: 'Annual' },
  { v: 'six_month', label: '6 months' },
  { v: 'twelve_month', label: '12 months' },
];
const BLANK = {
  product_key: 'regen_plus', product_name: 'YourReGen+', currency: '', symbol: '', market: '',
  monthly: '', annual: '', six_month: '', twelve_month: '', best_value: '', promo_label: '',
  checkout_supported: false, active: true, sort: 1,
};
const NUM = ['monthly', 'annual', 'six_month', 'twelve_month'];

export default function AdminLandingPricing() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get('/admin/landing-pricing').then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Remove pricing row for ${name}?`)) return;
    await api.delete(`/admin/landing-pricing/${id}`); toast.success('Removed'); load();
  };

  const grouped = (rows || []).reduce((acc, r) => { (acc[r.product_key] ||= []).push(r); return acc; }, {});

  return (
    <div className="space-y-5" data-testid="admin-landing-pricing">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mini text-[#00C8FF]">Monetization</div>
          <h1 className="font-display font-black text-3xl mt-1 flex items-center gap-2"><Tag className="w-7 h-7 text-[#00C8FF]" /> Website Pricing</h1>
          <p className="text-sm text-muted-foreground mt-1">Prices shown on the public YourReGen website (YourReGen+ &amp; Protocol). Changes appear instantly — no redeploy. Checkout &amp; billing are unaffected.</p>
        </div>
        <Button onClick={() => setEdit({ ...BLANK, _new: true })} data-testid="add-landing-price-btn" className="bg-[#00C8FF] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Price</Button>
      </div>

      {!rows ? <div className="text-muted-foreground py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([pkey, list]) => (
            <div key={pkey}>
              <div className="font-display font-bold text-lg mb-2">{PRODUCTS[pkey] || pkey}</div>
              <div className="space-y-2">
                {list.map(r => (
                  <div key={r.id} className="regen-card-elevated p-4" data-testid={`lp-${r.product_key}-${r.currency}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold flex items-center gap-2 flex-wrap">
                          {r.currency} <span className="text-[11px] text-muted-foreground">({r.market})</span>
                          {r.best_value && <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[10px]">BEST VALUE: {r.best_value}</span>}
                          {r.checkout_supported ? <span className="pill bg-[#00C8FF]/15 text-[#00C8FF] text-[10px]">Checkout ready</span> : <span className="pill bg-[var(--c-film)] text-muted-foreground text-[10px]">Display only</span>}
                          {!r.active && <span className="pill bg-[var(--c-film)] text-muted-foreground text-[10px]">Inactive</span>}
                        </div>
                        <div className="text-[12px] text-muted-foreground mt-1">
                          {r.monthly ? `${r.symbol}${r.monthly}/mo` : ''}{r.annual ? ` · ${r.symbol}${r.annual}/yr` : ''}{r.six_month ? ` · 6mo ${r.symbol}${r.six_month}` : ''}{r.twelve_month ? ` · 12mo ${r.symbol}${r.twelve_month}` : ''}{r.promo_label ? ` · “${r.promo_label}”` : ''}
                        </div>
                      </div>
                      <Button size="icon" variant="outline" className="border-[var(--c-hair)]" onClick={() => setEdit(r)} data-testid={`lp-edit-${r.product_key}-${r.currency}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-400" onClick={() => del(r.id, `${r.product_name} ${r.currency}`)} data-testid={`lp-del-${r.product_key}-${r.currency}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <EditDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState(row);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = { ...f };
    NUM.forEach(k => payload[k] = parseFloat(payload[k]) || 0);
    payload.sort = parseInt(payload.sort) || 0;
    payload.product_name = PRODUCTS[payload.product_key] || payload.product_name;
    delete payload._new; delete payload.id; delete payload._id; delete payload.updated_at;
    try {
      if (row._new) await api.post('/admin/landing-pricing', payload);
      else await api.put(`/admin/landing-pricing/${row.id}`, payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); setBusy(false); }
  };
  const fld = (k, label, ph, type = 'text') => (<div><Label>{label}</Label><Input type={type} value={f[k] ?? ''} placeholder={ph} onChange={e => set(k, e.target.value)} className="bg-transparent border-[var(--c-hair)]" data-testid={`lp-f-${k}`} /></div>);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[88vh] overflow-y-auto" data-testid="landing-pricing-edit-dialog">
        <DialogHeader><DialogTitle>{row._new ? 'Add Price' : `Edit ${row.product_name} · ${row.currency}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Product</Label>
            <select value={f.product_key} onChange={e => { set('product_key', e.target.value); set('product_name', PRODUCTS[e.target.value]); set('sort', e.target.value === 'protocol' ? 2 : 1); }} className="w-full bg-transparent border border-[var(--c-hair)] rounded-md h-10 px-3 text-sm" data-testid="lp-f-product_key">
              {Object.entries(PRODUCTS).map(([k, v]) => <option key={k} value={k} className="bg-[var(--c-elev)]">{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">{fld('currency', 'Currency', 'USD')}{fld('symbol', 'Symbol', 'US$')}{fld('market', 'Market', 'United States')}</div>
          <div className="grid grid-cols-2 gap-3">{fld('monthly', 'Monthly price', '14.99', 'number')}{fld('annual', 'Annual price', '79.99', 'number')}</div>
          <div className="grid grid-cols-2 gap-3">{fld('six_month', '6-month total', '474', 'number')}{fld('twelve_month', '12-month total', '708', 'number')}</div>
          <div>
            <Label>Best value badge</Label>
            <select value={f.best_value || ''} onChange={e => set('best_value', e.target.value)} className="w-full bg-transparent border border-[var(--c-hair)] rounded-md h-10 px-3 text-sm" data-testid="lp-f-best_value">
              {BEST_VALUES.map(b => <option key={b.v} value={b.v} className="bg-[var(--c-elev)]">{b.label}</option>)}
            </select>
          </div>
          {fld('promo_label', 'Promo label (optional)', 'Launch offer')}
          <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3"><div className="flex-1 text-sm">Checkout supported (currency billable)</div><Switch checked={!!f.checkout_supported} onCheckedChange={v => set('checkout_supported', v)} data-testid="lp-f-checkout" /></div>
          <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3"><div className="flex-1 text-sm">Active (shown on website)</div><Switch checked={!!f.active} onCheckedChange={v => set('active', v)} data-testid="lp-f-active" /></div>
          <Button onClick={save} disabled={busy} className="w-full bg-[#00C8FF] text-[#081726] font-semibold" data-testid="lp-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
